import { Component, inject, input, OnChanges, OnInit, output, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { AccountingModelDetailModel } from 'src/app/models/management/accounting-model-detail.model';
import { AccountingModelModel } from 'src/app/models/management/accounting-model.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { AccountingModelDetailCaptureTableModel } from 'src/app/models/tables/management/accounting-model-detail-capture-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { AccountingModelDetailService } from 'src/app/services/management/accounting-model-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-accounting-model-detail',
  templateUrl: './accounting-model-detail.component.html',
  styleUrls: ['./accounting-model-detail.component.scss'],
  standalone: false
})
export class AccountingModelDetailComponent implements OnInit, OnChanges {

  private readonly accountingModelDetailService = inject(AccountingModelDetailService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  accountingModelModel = input<AccountingModelModel>(new AccountingModelModel());
  disabledDetail = input<boolean>(false);
  updateListDetail = input<boolean>(false);
  updateList = output<void>();

  accountingModelDetailModel: AccountingModelDetailModel = new AccountingModelDetailModel();
  ascendant: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  detailData: PagedResponseModel<AccountingModelDetailCaptureTableModel> = new PagedResponseModel<AccountingModelDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  isFormView: boolean = false;
  limit: number = 10;
  page: number = 1;
  tableActions: TableAction[] = [];

  async ngOnInit(): Promise<void> {
    await this.getColumns();
    this.tableActions = this.getDefaultActions();
  }

  ngOnChanges(changes: SimpleChanges) {
    const updateListDetail = changes['updateListDetail'];
    if (updateListDetail && !updateListDetail.firstChange) {
      this.getAccountingModelDetailsByAccountingModel();
    }
    const accountingModelModel = changes['accountingModelModel'];
    if (accountingModelModel && !accountingModelModel.firstChange) {
      this.getFormView(false);
      this.getAccountingModelDetailsByAccountingModel();
      this.accountingModelDetailModel = new AccountingModelDetailModel();
    }
  }

  createNewDetail() {
    if (this.accountingModelModel().accountingModelId === 0) {
      this.toastHandlerService.showToast('warning', 'El modelo contable aún no se ha guardado.');
      return;
    }
    this.isFormView = true;
    this.accountingModelDetailModel = new AccountingModelDetailModel();
    this.accountingModelDetailModel.accountingModelId = this.accountingModelModel().accountingModelId;
  }

  async deleteAccountingModelDetail(selectedIds: number[]) {
    if (selectedIds.length === 0) {
      return;
    }
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar el detalle del modelo contable?');
    if (accepted) {
      try {
        await firstValueFrom(this.accountingModelDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast('success', 'El detalle fue eliminado exitosamente.');
        this.clearSelectionTrigger++;
        this.getUpdateListDetailByDelete();
      } catch (error) {
        this.getUpdateListDetailByDelete();
        console.error('Error al eliminar los detalles del modelo contable.', error);
      }
    }
  }

  async getAccountingModelDetailById(accountingModelDetailId: number) {
    try {
      this.accountingModelDetailModel = await firstValueFrom(
        this.accountingModelDetailService.getAccountingModelDetailById(accountingModelDetailId)
      );
    } catch (error) {
      console.error('Error al obtener el detalle del modelo contable por ID.', error);
    }
  }

  async getAccountingModelDetailsByAccountingModel() {
    if (this.accountingModelModel().accountingModelId === 0) {
      this.detailData = new PagedResponseModel<AccountingModelDetailCaptureTableModel>();
      return;
    }
    try {
      this.detailData = await firstValueFrom(
        this.accountingModelDetailService.getAccountingModelDetailsForCaptureTable(
          this.page, this.limit, this.columnSort, this.ascendant, this.accountingModelModel().accountingModelId
        )
      );
    } catch (error) {
      console.error('Error al obtener los detalles del modelo contable.', error);
    }
  }

  async getColumns() {
    try {
      this.detailHeader = await firstValueFrom(this.accountingModelDetailService.getColumnsForCaptureTable());
    } catch (error) {
      console.error('Error al obtener las columnas para la tabla del detalle.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        action: 'edit'
      },
    ];
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  getUpdateListDetailByDelete() {
    this.accountingModelDetailModel = new AccountingModelDetailModel();
    this.clearSelectionTrigger++;
    this.getAccountingModelDetailsByAccountingModel();
    this.updateList.emit();
  }

  async onAction(event: { action: string, item?: AccountingModelDetailCaptureTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.getFormView(true);
      await this.getAccountingModelDetailById(event.item.id);
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteAccountingModelDetail(event.selectedItems);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getAccountingModelDetailsByAccountingModel();
  }

}
